/**
 * Fleet markers on the galaxy map: layout of several fleets around one star and the tap hit-test (pure, unit-tested).
 *
 * Every number is in CSS pixels on the screen, so a finger-sized target stays finger-sized at every zoom:
 * the hit radius is 22 px (a 44 px target) although the drawn marker is smaller. The nearest marker wins, so
 * markers that overlap never steal each other's taps: each owns the part of the plane closest to it.
 */

/** Radius of a fleet marker's tap target (CSS px): 44 px diameter. */
export const FLEET_HIT_RADIUS = 22;
/** Most fleets a system shows spread out; more (or a far zoom level) fold into one counted stack. */
export const MAX_FAN = 6;
/** Distance between the centres of neighbouring markers in a fan (px). */
export const FAN_SPACING = 34;
/** The first marker of a system sits up and to the right of the star, the way the single marker always did. */
export const BASE_OFFSET = { dx: 14, dy: -14 } as const;

export interface Offset { dx: number; dy: number }

/** Screen offsets (from the star) of `n` markers: a short arc, wide enough that neighbours stay `FAN_SPACING` apart. */
export function fanSlots(n: number): Offset[] {
  if (n <= 0) return [];
  if (n === 1) return [{ dx: BASE_OFFSET.dx, dy: BASE_OFFSET.dy }];
  const r = Math.max(26, FAN_SPACING / (2 * Math.sin(Math.PI / n)));
  const step = Math.min((2 * Math.PI) / n, 2 * Math.asin(Math.min(1, FAN_SPACING / (2 * r)))); // chord, not arc, length
  const start = -Math.PI / 4; // first marker at the upper right, the fan runs clockwise
  const out: Offset[] = [];
  for (let i = 0; i < n; i++) {
    const a = start + i * step;
    out.push({ dx: Math.cos(a) * r, dy: Math.sin(a) * r });
  }
  return out;
}

export type HitKind = 'fleet' | 'foreign' | 'stack';
export interface HitTarget {
  kind: HitKind;
  /** fleet id (kind fleet / foreign) or system id (kind stack) */
  id: string;
  systemId: string;
  /** screen position of the marker centre */
  x: number;
  y: number;
}

/** The target closest to (x, y) within `radius`; on an exact tie the later (drawn on top) one wins. */
export function hitTest(targets: readonly HitTarget[], x: number, y: number, radius = FLEET_HIT_RADIUS): HitTarget | null {
  let best: HitTarget | null = null;
  let bestD = Infinity;
  for (const t of targets) {
    const d = Math.hypot(t.x - x, t.y - y);
    if (d <= radius && d <= bestD) { best = t; bestD = d; }
  }
  return best;
}

export interface ScreenRect { x0: number; y0: number; x1: number; y1: number }

/** Normalises a drag from (ax, ay) to (bx, by) into a rectangle. */
export function rectFrom(ax: number, ay: number, bx: number, by: number): ScreenRect {
  return { x0: Math.min(ax, bx), y0: Math.min(ay, by), x1: Math.max(ax, bx), y1: Math.max(ay, by) };
}

/** Own fleets (never foreign ones, never stacks) whose marker centre is inside the box. */
export function fleetsInRect(targets: readonly HitTarget[], r: ScreenRect): string[] {
  return targets.filter(t => t.kind === 'fleet' && t.x >= r.x0 && t.x <= r.x1 && t.y >= r.y0 && t.y <= r.y1).map(t => t.id);
}

/**
 * A counted stack replaces the fan when a system has more fleets than fit, or the map is zoomed far out;
 * tapping the stack opens it (`expanded`), and then every fleet is its own marker, however many there are.
 */
export function shouldStack(count: number, farLod: boolean, expanded: boolean): boolean {
  if (count <= 1 || expanded) return false;
  return count > MAX_FAN || farLod;
}
